import type { RouteLocationNormalized } from "vue-router"

import { useUserStore } from "@/stores/user"
import checkPermissionConfig from "@/utils/checkPermissionConfig/index"
import { routeParamString } from "@/utils/routeParam"

interface RouterProtectOptions<TApi, TFormatted = TApi> {
    to: RouteLocationNormalized
    goBackPath?: { name: string }
    PERMISSIONS: string | string[]
    api: (id: string) => Promise<{ data: TApi }>
    formatFunction?: (response: { data: TApi }) => {
        data: TFormatted
    }
}

interface AssetResponse {
    creator?: {
        department?: { uuid?: string }
        uuid?: string
        role?: { level?: number }
    }
    status?: string
    [key: string]: unknown
}

export default async function useRouterProtect<TApi, TFormatted = TApi>({
    to,
    goBackPath = { name: "forbidden-index" },
    PERMISSIONS,
    api,
    formatFunction,
}: RouterProtectOptions<TApi, TFormatted>): Promise<[true, { data: TApi }] | [{ name: string }]> {
    try {
        const response = await api(routeParamString(to.params.id))
        const formatResponse = formatFunction ? formatFunction(response) : (response as unknown as { data: TFormatted })
        const userStore = useUserStore()
        const data = formatResponse.data as AssetResponse

        const hasPermissions = checkPermissionConfig(PERMISSIONS, userStore, {
            department: data.creator?.department?.uuid,
            status: data.status,
            creatorUuid: data.creator?.uuid,
            roleLevel: data.creator?.role?.level,
        })

        if (!hasPermissions) return [goBackPath]
        return [true, response]
    } catch (error) {
        console.error(error)
        return [goBackPath]
    }
}
